import React from 'react';
import ApplePay from '@adyen/adyen-web/dist/types/components/ApplePay';
import { useAdyenEnvironment } from './adyen_environment_hook';
import { useFiveDigitLocale } from './user_five_digit_locale_hook';
import { ApplePayCoreClient } from '../lib/apple_pay_core_client';
import { core_apimessages_CheckoutPaymentResponse } from '../schemas/rql';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { reverbPaymentsPresentmentCurrency } from '@reverbdotcom/commons/src/currency';
import { PaymentAmount } from '@adyen/adyen-web/dist/types/types';
import { adyenCheckout } from '../lib/adyen_checkout';

interface Props {
  paymentMethodsConfig: string;
  listingCurrencyCode: string;
}

export function useApplePay({ paymentMethodsConfig, listingCurrencyCode }: Props) {
  const { clientKey, environment } = useAdyenEnvironment();
  const { currency: buyerCurrencyCode } = useUser();
  const locale = useFiveDigitLocale();
  const [applePay, setApplePay] = React.useState<ApplePay>();
  const [loading, setLoading] = React.useState(true);
  const [isAvailable, setAvailable] = React.useState(false);
  const [paymentResult, setPaymentResult] = React.useState<core_apimessages_CheckoutPaymentResponse>({});

  function openPaymentSheet(e) {
    e.preventDefault();
    applePay.submit();
  }

  function finishLoading() {
    setLoading(false);
  }

  React.useEffect(() => {
    const configuration = {
      paymentMethodsResponse: JSON.parse(paymentMethodsConfig),
      amount: zeroValueAmount(buyerCurrencyCode, listingCurrencyCode),
      clientKey,
      environment,
      locale,
    };

    const checkout = adyenCheckout({ configuration });

    setApplePayFromCheckout({ checkout, setApplePay, setAvailable, setPaymentResult, listingCurrencyCode, finishLoading });

  }, [clientKey, environment, locale, paymentMethodsConfig, buyerCurrencyCode, listingCurrencyCode]);

  return { openPaymentSheet, loading, isAvailable, paymentResult };
}

export async function setApplePayFromCheckout({
  checkout,
  listingCurrencyCode,
  setApplePay,
  setAvailable,
  finishLoading,
  setPaymentResult,
}: {
  checkout: any;
  listingCurrencyCode: string;
  setApplePay: (applePay: ApplePay) => void;
  setAvailable: (isAvailable: boolean) => void;
  finishLoading: () => void;
  setPaymentResult: (result: core_apimessages_CheckoutPaymentResponse) => void;
}) {
  const applePayCoreClient = new ApplePayCoreClient(listingCurrencyCode);

  async function onShippingContactSelected(
    resolve: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
    _reject: (update: ApplePayJS.ApplePayShippingContactUpdate) => void,
    event: ApplePayJS.ApplePayShippingContactSelectedEvent,
  ) {
    const { total, lineItems } = await applePayCoreClient.fetchLineItems(event.shippingContact);
    resolve({ newTotal: total, newLineItems: lineItems });
  }

  async function onAuthorized(resolve, _reject, event) {
    const result = await applePayCoreClient.makePayment(event.payment);
    resolve();
    setPaymentResult(result);
  }

  const applePay = checkout.create('applepay', {
    onShippingContactSelected,
    onAuthorized,
    totalPriceStatus: 'pending',
    requiredShippingContactFields: ['postalAddress', 'name', 'phone'],
  });

  try {
    setApplePay(applePay);
    setAvailable(await applePay.isAvailable());
    finishLoading();
  } catch {
    finishLoading();
  }
}

export function zeroValueAmount(buyerCurrencyCode: string, orderCurrencyCode: string): PaymentAmount {
  return { value: 0, currency: reverbPaymentsPresentmentCurrency(buyerCurrencyCode, orderCurrencyCode) };
}
